import React, { useContext, useEffect } from "react"
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native"
import { useDispatch, useSelector } from "react-redux"
import Animated, { FadeIn } from "react-native-reanimated"
import { LinearGradient } from "expo-linear-gradient"
import { FontAwesome5 } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import TextM from "@common/components/text/TextM"
import ChampionBubble from "../../../student/champions/ChampionBubble"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { closeAutoMode, setAutoModeEval } from "@data/redux/actions/liveMode"
import AutoModeCloseButton from "./AutoModeCloseButton"

const OPTIONS = [
    {
        key: "got_it",
        label: "Tout compris !",
        icon: "check",
        color: theme.colors.success,
    },
    {
        key: "mixed",
        label: "Pas tout compris",
        icon: "question",
        color: theme.colors.warning,
    },
    {
        key: "lost",
        label: "Bloqué, j'ai besoin d'aide",
        icon: "times",
        color: theme.colors.error,
    },
]

const AutoEvalOverlay = () => {
    const dispatch = useDispatch()
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()
    const visible = useSelector((s) => s.liveMode?.autoMode?.showEval)
    const isLiveModeEnabled = useSelector(
        (s) => s.liveMode?.isLiveModeEnabled
    )
    // Play the Sam intro jingle on overlay open — matches SamConversationBase
    // pattern (same sound when Sam first speaks during a checkpoint).
    useEffect(() => {
        if (visible) {
            PlaySoundM.play(
                require("../../../../assets/sounds/jingles_PIZZI14.mp3")
            )
        }
    }, [visible])

    if (!visible) return null

    const handleSelect = (selectedEval) => {
        // We don't notify the tutor here: a single event is emitted later, when
        // the student actually picks an activity (AutoModeChoiceModal), carrying
        // both the self-eval and the chosen activity. See useTutorEventsQueue.
        dispatch(setAutoModeEval(selectedEval))
    }

    const handleDismiss = () => {
        dispatch(closeAutoMode())
    }

    const footerSpace = isLiveModeEnabled ? 70 + insets.bottom : insets.bottom
    const isWeb = Platform.OS === "web"
    const isLargeScreen = isWeb && (dimensions?.width || 0) >= 800
    const bubbleWidth = isLargeScreen
        ? Math.min((dimensions?.width || 0) - 80, 720)
        : (dimensions?.width || 0) - 40

    return (
        <Animated.View
            entering={FadeIn.duration(300)}
            style={[
                styles.overlayContainer,
                {
                    width: "100%",
                    height: (dimensions?.height || 0) - footerSpace,
                    top: 0,
                    left: 0,
                    paddingTop: insets.top,
                },
            ]}
        >
            <View style={styles.backdrop} />
            <LinearGradient
                colors={["#F8FAFC", "#F1F5F9", "#E2E8F0"]}
                style={StyleSheet.absoluteFillObject}
            />

            <AutoModeCloseButton visible={visible} onPress={handleDismiss} />

            <View style={styles.contentContainer}>
                <ScrollView
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <ChampionBubble
                        name="sam"
                        variant="main"
                        championPosition="left"
                        texts={["Cette activité, comment ça s'est passé ?"]}
                        width={bubbleWidth}
                        animated={false}
                        isModal={false}
                    />

                    <View style={[styles.optionsContainer, { width: bubbleWidth }]}>
                        {OPTIONS.map((opt) => (
                            <TouchableOpacity
                                key={opt.key}
                                onPress={() => handleSelect(opt.key)}
                                style={styles.optionItem}
                                activeOpacity={0.85}
                            >
                                <TextM
                                    fontWeight="SemiBold"
                                    color={theme.colors.dark}
                                    wrap
                                    style={{ flex: 1 }}
                                >
                                    {opt.label}
                                </TextM>
                                <View
                                    style={[
                                        styles.iconBadge,
                                        {
                                            backgroundColor: `${opt.color}20`,
                                        },
                                    ]}
                                >
                                    <FontAwesome5
                                        name={opt.icon}
                                        size={18}
                                        color={opt.color}
                                    />
                                </View>
                            </TouchableOpacity>
                        ))}
                    </View>
                </ScrollView>
            </View>
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    overlayContainer: {
        position: "absolute",
        zIndex: 8,
        elevation: 8,
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
    contentContainer: {
        flex: 1,
        zIndex: 2,
    },
    scrollContent: {
        ...(Platform.OS === "web" ? { flexGrow: 1 } : { flex: 1 }),
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: 20,
        paddingBottom: 80,
        paddingHorizontal: 20,
    },
    optionsContainer: {
        marginTop: 20,
        alignSelf: "center",
    },
    optionItem: {
        backgroundColor: theme.colors.white,
        borderRadius: 18,
        padding: 18,
        marginVertical: 8,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        minHeight: 60,
        shadowColor: theme.colors.grey,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 4,
    },
    iconBadge: {
        padding: 10,
        borderRadius: 12,
    },
})

export default AutoEvalOverlay
